import { incotermRequiresAdjustments, isAdjustmentsRoute } from '../legacy-components/Adjustments/utils';
import { isFreshwaysEori } from './companies';
import { ValidateAllLoadAdjustments } from '../legacy-components/Util/ValidateLoadAdjustments';
import { getIsValidJiraTicket } from './stringValidation';

const checkControlledGoods = (value) => {
  //check if store.controlledGoods[0] is 'N' or 'Y' or 'n' or 'y'. If that's the case, return true, otherwise return false
  return value === 'N' || value === 'Y' || value === 'n' || value === 'y';
};

const checkAdjustments = (adjustments) => {
  for (let adjustment of adjustments) {
    //we validate the adjustments based on the incoterm - if they're required and not filled, we show an error message.
    const adjustmentIncompleteValue = !parseFloat(adjustment.amount) || adjustment.currency === '';
    if (adjustment.required && adjustmentIncompleteValue) {
      return true;
    }
  }
  return false;
};

const isEmpty = (data, message = 'Field is required.') =>
  data === null ||
  data === undefined ||
  data === '' ||
  data === 0 ||
  (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0)
    ? message
    : '';

const containerValidation = (data) => {
  const containerRegex = /^[A-Za-z]{4}\d{7}$/;
  const isContainerInCorrectFormat = containerRegex.test(data);
  if (isContainerInCorrectFormat) return '';
  if (isEmpty(data)) return '';
  return 'Invalid container number. Leave blank or enter in format ABCD1234567';
};

export const jiraTicketValidation = (ticketName, isRequired) => {
  if (isRequired && !ticketName) {
    return 'Jira Ticket is mandatory for this Business Partner.';
  }

  return !ticketName || getIsValidJiraTicket(ticketName)
    ? ''
    : 'Jira ticket must be in format CCC-NUMBER (11 characters total, e.g. CCC-1234567)';
};

const poValidation = (poNumber, parentEori) => {
  if (isFreshwaysEori(parentEori)) {
    return isEmpty(poNumber) ? 'Please check CCC ticket is valid for Freshways' : '';
  }
  return '';
};

export const isValidDate = (date) => {
  if (!date) return false;

  // Force local parsing
  const d = new Date(date + 'T00:00:00');

  if (isNaN(d)) return false;

  const now = new Date();
  const diff = Math.abs(now - d);
  const max = 365 * 24 * 60 * 60 * 1000;

  return diff <= max;
};

const exceedsMaxLength = (value, maxLength, message) => {
  return value && value.length > maxLength ? message : '';
};

const consignorValidation = (
  isMultipleConsignors,
  currentTab,
  isMultipleConsignorsValid,
  consignorData,
  QAConsignorData
) => {
  if (isMultipleConsignors) {
    return '';
  }

  return isEmpty(currentTab === 3 ? QAConsignorData : consignorData, 'Please select a Consignor');
};

export const isLoadTypeEPO = (headers) => {
  return headers?.[0]?.loadType === 'EPO' || headers?.[0]?.loadstatus === 'EPO';
};

/**
 * Validates Header for Unified Load Form
 *
 * Mostly a duplicate of the headerContainsErrors function (which will eventually be removed)
 */

export const unifiedHeaderContainsErrors = (store, qaStore, currentTab, isHybrid) => {
  const unifiedLoadDataState = qaStore?.unifiedLoadData?.[0] ?? {};
  const isEPOLoad = isLoadTypeEPO(unifiedLoadDataState?.Headers);

  const entryPortRequired = !store.RowExport[0] && !(isEPOLoad && store.GBexport[0]);
  const exitPortRequired = store.ffd[0] === false && !store.RowImport[0] && !(isEPOLoad && store.GBimport[0]);

  const controlledGoodsRequired = store.ffd[0] === false && store.GBimport[0];

  const portModelRequired =
    isHybrid &&
    (store.GBimport[0] || store.GBexport[0]) &&
    (Number(store.importMixedPortsModal[0]) === 1 || Number(store.exportMixedPortsModal[0]) === 1);

  const isAdjustmentRequired =
    isAdjustmentsRoute(store.directionSelected[0]) && incotermRequiresAdjustments(store.selectincoterm[0]?.toString());
  const allAdjustmentsAreValid = ValidateAllLoadAdjustments(store.adjustments[0]);
  const parentEori = store.newLoadParent[0]?.parentEori;

  const isMultipleConsignors = store.multipleConsignors[0];
  const isMultipleInvoices = store.multipleInvoices[0];

  const fields = [
    {
      field: 'container',
      name: 'Container',
      hasError: containerValidation(store.container[0]),
    },
    {
      field: 'directionSelected',
      name: 'Route',
      hasError: isEmpty(store.directionSelected[0], 'Please select a Route'),
    },
    {
      field: 'despatch',
      name: 'Country of Despatch',
      hasError: isEmpty(store.despatch[0], 'Please select a Country'),
    },
    {
      field: 'destination',
      name: 'Country of Destination',
      hasError: isEmpty(store.destination[0], 'Please select a Country'),
    },
    ...(entryPortRequired
      ? [
          {
            field: 'entry',
            name: 'Entry Port',
            hasError: isEmpty(store.entry[0], 'Please select an Entry Port'),
          },
        ]
      : []),
    ...(exitPortRequired
      ? [
          {
            field: 'exit',
            name: 'Exit Port',
            hasError: isEmpty(store.exit[0], 'Please select an Exit Port'),
          },
        ]
      : []),
    ...(portModelRequired
      ? [
          {
            field: 'portsModal',
            name: 'Port Model',
            hasError: isEmpty(store.portsModal[0], 'Please select a Port Model'),
          },
        ]
      : []),
    {
      field: 'consignor',
      name: 'Consignor Details',
      hasError: consignorValidation(
        isMultipleConsignors,
        currentTab,
        store.isMultipleConsignorsValid[0],
        store.consignor?.[0],
        qaStore?.consignorData?.[0]
      ),
    },
    {
      field: 'consignee',
      name: 'Consignee Details',
      hasError: isEmpty(
        currentTab === 3 ? qaStore?.consigneeData?.[0] : store?.consignee[0],
        'Please select a Consignee'
      ),
    },
    {
      field: 'shipStatus',
      name: 'Shipping Status',
      hasError: isEmpty(store.shipStatus[0], 'Please select a Shipping Status'),
    },
    {
      field: 'truck',
      name: 'Truck / Trailer Number',
      hasError:
        isEmpty(store.truck[0], 'Please enter Truck / Trailer Number') ||
        exceedsMaxLength(store.truck[0], 35, 'Truck / Trailer Number cannot exceed 35 characters'),
    },
    {
      field: 'date',
      name: 'Date of Loading',
      hasError: isValidDate(store.date[0]) ? '' : 'Please enter a date within 365 days.',
    },
    {
      field: 'jiraTicket',
      name: 'Jira Ticket',
      hasError: jiraTicketValidation(store.jiraTicket[0], store.isJiraTicketRequired[0]),
    },
    {
      field: 'jobRef',
      name: 'Job Reference',
      hasError:
        isEmpty(store.jobRef[0], 'Please enter Job Reference') ||
        exceedsMaxLength(store.jobRef[0], 35, 'Job Reference cannot exceed 35 characters'),
    },
    {
      field: 'invoicenumber',
      name: 'Invoice Number',
      hasError:
        !isMultipleInvoices &&
        (isEmpty(store.invoicenumber[0], 'Please enter Invoice Number') ||
          exceedsMaxLength(store.invoicenumber[0], 35, 'Invoice Number cannot exceed 35 characters')),
    },
    {
      field: 'poNumber',
      name: 'PO Number',
      hasError: poValidation(store.poNumber[0], parentEori),
    },
    {
      field: 'invoicecurr',
      name: 'Invoice Currency',
      hasError: isEmpty(store.invoicecurr[0], 'Please select an Invoice Currency'),
    },
    {
      field: 'selectincoterm',
      name: 'Incoterm',
      hasError: isEmpty(store.selectincoterm[0], 'Please select an Incoterm'),
    },
    {
      field: 'incotermLocation',
      name: 'Incoterm Location',
      hasError: isEmpty(store.incotermLocation[0], 'Please enter an Incoterm Location'),
    },
    ...(controlledGoodsRequired
      ? [
          {
            field: 'controlledGoods',
            name: 'Controlled Goods',
            hasError: checkControlledGoods(store.controlledGoods[0]) ? '' : 'Please select an option',
          },
        ]
      : []),
    ...(Number.parseInt(store.directionSelected[0]) === 1
      ? [
          {
            field: 'entry2',
            name: 'Entry Port 2',
            hasError: isEmpty(store.entry2[0], 'Please select an Entry Port'),
          },
          {
            field: 'exit2',
            name: 'Exit Port 2',
            hasError: isEmpty(store.exit2[0], 'Please select an Exit Port'),
          },
        ]
      : []),
    ...(isAdjustmentRequired || !allAdjustmentsAreValid
      ? [
          {
            field: 'adjustments',
            name: 'Adjustments (Freight Charges)',
            hasError: checkAdjustments(store.adjustments[0]) ? 'Please complete required adjustments' : '',
          },
        ]
      : []),
  ];
  return {
    hasErrors: fields.some((field) => field.hasError),
    fieldsWithErrors: fields.filter((field) => field.hasError),
  };
};

export const isLoadStatusSubmitted = (loadStatus = '') => {
  if (!loadStatus) return false;
  return ['in progress', 'completed', 'cancelled', 'submitted', 'amendment required'].includes(
    loadStatus?.toLowerCase()
  );
};

export const showUpdateStatusOption = (loadStatus = '') => {
  if (!loadStatus) return false;
  return ['draft', 'cm8 draft', 'new', 'on hold (potential duty)', 'waiting for customer'].includes(
    loadStatus?.toLowerCase()
  );
};

export const showAmendmentRequiredOption = (loadStatus = '', sfdHasMrn = false) => {
  if (!loadStatus) return false;
  return loadStatus?.toLowerCase() === 'in progress' && sfdHasMrn;
};

/** GB import routes: ROI/EU/ROW → GB (multiple consignors). */
const MOVEMENT_TYPES_MULTIPLE_CONSIGNORS = new Set([2, 13, 10, 11, '2', '13', '10', '11']);

/** Routes that support multiple invoices (GB import + GB export). */
const MOVEMENT_TYPES_MULTIPLE_INVOICES = new Set([
  2,
  13,
  10,
  11,
  '2',
  '13',
  '10',
  '11',
  3,
  6,
  7,
  12,
  '3',
  '6',
  '7',
  '12',
]);

export const directionAllowsMultipleConsignors = (direction) => MOVEMENT_TYPES_MULTIPLE_CONSIGNORS.has(direction);

export const directionAllowsMultipleInvoices = (direction) => MOVEMENT_TYPES_MULTIPLE_INVOICES.has(direction);

export const formatUnifiedLoadInvReference = (value) =>
  String(value ?? '').toUpperCase() === 'MULTIPLE' ? 'Multiple' : value;

/** OPS, QA, and CMT can use unified build/import/CM8 and unified load screens (CABS-3350). */
export const canAccessUnifiedProject = (validateRole) =>
  validateRole({
    or: [{ department: 'CMT' }, { department: 'OPS' }, { department: 'QA' }],
  });

// success and error messages for cog actions on CABIE/EPO list load screens
export const statusMessages = (status) => {
  const messages = {
    'amendment required': {
      success: 'Flagged for Amendment',
      error: 'Failed to flag for Amendment',
    },
    cancelled: {
      success: 'Load cancelled successfully',
      error: 'Failed to cancel load',
    },
    restored: {
      success: 'Load restored successfully',
      error: 'Failed to restore load',
    },
    default: {
      success: 'Status updated successfully',
      error: 'Failed to update load status',
    },
  };

  return messages[status.toLowerCase()] ?? messages['default'];
};

// message to display in confirmation modal for cog actions on CABIE/EPO list load screens
export const confirmModalMessage = (type) => {
  if (type === 'amendment') {
    return 'Please confirm you want to flag this load as Amendment Required.';
  }
  return `Please confirm you want to ${type} this load.`;
};
